import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 72;
const APPBAR_HEIGHT = 64;

const AppShell = ({
  brand = 'Apex LMS',
  logoSrc = '/logo.png',
  navItems = [],
  headerTitle = 'Dashboard',
  topRight = null,
  children,
  collapseKey = 'appShellCollapsed',
  footer = null,
  userName = '',
  userEmail = '',
  userRole = '',
  userAvatar = '',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!collapseKey) return;
    try {
      const saved = localStorage.getItem(collapseKey);
      if (saved != null) setCollapsed(saved === 'true');
    } catch {}
  }, [collapseKey]);

  useEffect(() => {
    if (!collapseKey) return;
    try {
      localStorage.setItem(collapseKey, String(collapsed));
    } catch {}
  }, [collapsed, collapseKey]);

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const isActive = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    return location.pathname.startsWith(`${path}/`);
  };

  const renderBadge = (item) => {
    const count = Number(item.badgeCount || 0);
    const dot = Boolean(item.badgeDot);
    if (!count && !dot) return item.icon;
    return (
      <Badge
        color="error"
        variant={count ? 'standard' : 'dot'}
        badgeContent={count || undefined}
        overlap="circular"
      >
        {item.icon}
      </Badge>
    );
  };

  const footerContent = footer || (
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar
        src={userAvatar || undefined}
        sx={{ width: 40, height: 40, bgcolor: 'primary.light', color: 'primary.contrastText' }}
      >
        {userName ? userName.charAt(0).toUpperCase() : ''}
      </Avatar>
      {!collapsed && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {userName || 'User'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {userRole || userEmail || ''}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 2,
        }}
      >
        <Avatar
          src={logoSrc}
          variant="rounded"
          sx={{ width: 36, height: 36 }}
        />
        {!collapsed && (
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
            {brand}
          </Typography>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{ ml: 'auto' }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List>
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItemButton
                key={item.key || item.path}
                selected={active}
                onClick={() => {
                  if (item.path) navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(18, 166, 85, 0.12)',
                    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                      color: 'primary.main',
                      fontWeight: 700,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 44, color: active ? 'primary.main' : 'inherit' }}>
                  {renderBadge(item)}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {footerContent && <Box sx={{ p: 2 }}>{footerContent}</Box>}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          height: APPBAR_HEIGHT,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ minHeight: APPBAR_HEIGHT }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {typeof headerTitle === 'string' ? (
            <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
              {headerTitle}
            </Typography>
          ) : (
            <Box sx={{ flexGrow: 1 }}>{headerTitle}</Box>
          )}
          {topRight}
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: EXPANDED_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: (t) => `1px solid ${t.palette.divider}`,
              overflowX: 'hidden',
              transition: 'width 0.2s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        className="page-pad"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: `${APPBAR_HEIGHT}px`,
          mx: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppShell;
